import { useState, useCallback, useEffect, useMemo, memo } from 'react'
import styled from 'styled-components'
import { isMobile } from 'react-device-detect'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { getReviewableProduct, getOrderDetail } from '../../api/orderHistory'
import { getProductReviews } from '../../api/product'
import { getReviewBoards } from '../../api/home'
import ProductReviewItem from './ProductReviewItem'
import ProductDetailQna from './ProductDetailQna'
import Hr8 from '../hr/Hr8'
import { BtnGrayRounded } from '../Button'
import { useCommonContext } from '../provider/CommonProvider'
import { useMyContext } from '../provider/MyProvider'
import { useAppContext } from '../provider/AppProvider'
import { ProductDetailBaseInfo, ProductDetailType, ProductReviewsType } from '../../@types/product'
import { AFWebLinkType, RNPostMessage } from '../../js/postMessage'

interface ProductDetailReviewsProps {
  sectionRef: React.RefObject<HTMLDivElement>
  product: ProductDetailType
  productItem?: ProductDetailBaseInfo
  starCountArray: number[]
}

// 이미지 경로
const imgURL = '/images'

const ProductDetailReviews = ({ sectionRef, product, productItem, starCountArray }: ProductDetailReviewsProps) => {
  const [reviewKeyword, setReviewKeyword] = useState<string>('RECOMMEND')
  const [review, setReview] = useState<ProductReviewsType>()
  const [pageCount, setPageCount] = useState<number[]>([])
  const [page, setPage] = useState<number>(1)
  const [startPage, setStartPage] = useState<number>(1) // 페이지 그룹 시작 번호
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const { setIsLogin, hasAccessToken } = useCommonContext()
  const { setToastMessage, setToastBtnMessage, setToastEvent, setIsToast } = useMyContext()
  const { isAppView } = useAppContext()

  const param = useParams()
  const navigate = useNavigate()
  const location = useLocation()

  const isSort = false
  const maxPageButtons = 5 // 한 번에 표시할 페이지 버튼 수
  const totalPages = useMemo(() => pageCount.length, [pageCount])

  const getStarType = useCallback((reviewRate: number, idx: number) => {
    if (idx + 1 <= Math.ceil(reviewRate)) {
      return reviewRate - idx === 0.5 ? 'star_half_off' : 'star'
    }
    return 'star_off'
  }, [])

  const handleReviewWriteClick = useCallback(async () => {
    if (!hasAccessToken) {
      setIsToast(true)
      setToastMessage('로그인이 필요한 서비스입니다.')
      setToastBtnMessage('로그인')
      setToastEvent(() => () => setIsLogin(true))
      return
    }

    setIsLoading(true)

    try {
      const reviewableRes = await getReviewableProduct(Number(param.id))
      const item = reviewableRes?.data?.item[0]
      if (!reviewableRes.data.reviewable || !item || !item.orderStatus || item.orderStatus !== 'BUY_CONFIRM') {
        alert('구매확정 이후 리뷰 작성이 가능합니다.')
        return
      }
      const orderNo = item?.orderNo

      // 리뷰 가능할 때만 boards API 호출
      const boardsRes = await getReviewBoards()
      const reviewItem = boardsRes.data.items.find((item: any) => item.productNo === Number(param.id))
      if (isAppView) {
        RNPostMessage(AFWebLinkType.moveToLink, {
          url: `aboutfishing://aboutfishing.com/?page=webview&url=${process.env.REACT_APP_SHOP_URL}/reviewwrite/${param.id}?reviewNo=${reviewItem.reviewNo}&path=${location.pathname}&productNo=${param.id}&orderNo=${orderNo}`,
        })
      } else {
        navigate(`/reviewwrite/${param.id}`, {
          state: {
            reviewNo: reviewItem.reviewNo,
            productNo: param.id,
            path: location.pathname,
            orderNo,
          },
        })
      }
    } catch (err) {
      console.error(err)
    } finally {
      setIsLoading(false)
    }
  }, [
    hasAccessToken,
    param.id,
    navigate,
    location.pathname,
    setIsToast,
    setToastMessage,
    setToastBtnMessage,
    setToastEvent,
    setIsLogin,
    isAppView,
  ])

  const handlePrevClick = useCallback(() => {
    if (page > 1) {
      setStartPage((prevStartPage) => Math.max(prevStartPage - 1, 1))
      setPage((prevPage) => Math.max(prevPage - 1, 1))
    }
  }, [page])

  const handleNextClick = useCallback(() => {
    if (page < totalPages) {
      setPage((prevPage) => {
        const newPage = Math.min(prevPage + 1, totalPages)
        if (newPage > startPage + maxPageButtons - 1) {
          setStartPage((prevStartPage) => prevStartPage + 1)
        }
        return newPage
      })
    }
  }, [page, totalPages, startPage, maxPageButtons])

  // 상품 리뷰 가져오기
  useEffect(() => {
    if (!param.id) return
    getProductReviews(param.id, page, reviewKeyword)
      .then((res) => setReview(res.data))
      .catch((err) => console.log(err))
  }, [param, page, reviewKeyword])

  //리뷰 카운트
  useEffect(() => {
    if (!product) return
    const pageCountArray = Array.from({ length: Math.ceil(product.counter?.reviewCnt / 3) }, (_, i) => i)
    setPageCount(pageCountArray)
  }, [product])

  const renderPageGroupButtons = useCallback(() => {
    return Array.from({ length: Math.min(maxPageButtons, totalPages - startPage + 1) }, (_, idx) => {
      const currentPage = startPage + idx
      return (
        <button key={currentPage} className={currentPage === page ? 'on' : ''} onClick={() => setPage(currentPage)}>
          {currentPage}
        </button>
      )
    })
  }, [maxPageButtons, totalPages, startPage, page])

  // NOTE: 크리마 리뷰 주석 처리. 테스트 하려면 해당 소스 주석 해제 필요
  // useEffect(() => {
  //   const linkURL: string | null = null

  //   const handleMessage = (event: MessageEvent) => {
  //     // 문자열로 들어오는 경우(크리마 위젯 등)
  //     if (typeof event.data === 'string') {
  //       if (event.data.includes('act=open_new_review_popup')) {
  //         if (isAppView) {
  //           // RNPostMessage(AFWebLinkType.moveToLink, {
  //           //   url: `aboutfishing://aboutfishing.com/?page=webview&url=${linkURL}`,
  //           // })
  //           return
  //         }
  //       }
  //     }
  //   }

  //   const observer = new MutationObserver(() => {
  //     if (!isAppView) return
  //     const iframes = document.querySelectorAll('#crema-review-popup') as NodeListOf<HTMLIFrameElement>
  //     // NOTE: 앱 분기 추가예정
  //     if (iframes.length > 0) {
  //       iframes.forEach((el) => {
  //         el.style.display = 'none'
  //         el.style.opacity = '0'
  //         el.style.pointerEvents = 'none'
  //         el.parentNode?.removeChild(el)
  //       })
  //       // 이벤트 감지를 종료 할 시 아래 코드 활성화
  //       // observer.disconnect()
  //     }
  //   })
  //   observer.observe(document.body, { childList: true, subtree: true })

  //   // 팝업 종료 이벤트 처리
  //   // window.cremaOnEvent = (event: any) => {
  //   //   console.log('event >>> ', event)
  //   // }

  //   window.addEventListener('message', handleMessage)

  //   return () => {
  //     observer.disconnect()
  //     window.removeEventListener('message', handleMessage)
  //   }
  // }, [])

  // NOTE: 테스트
  // iframe_id=crema-product-reviews-3&act=open_new_review_popup&product_code=120846109&product_id=580&reload_target_id=crema-product-reviews-3&review_source=24&source_iframe_id=crema-product-reviews-3&url=%2Fshop.aboutfishing.kr%2Freviews%2Fnew%3Fno_pending_reviews%3Dtrue%26product_id%3D580%26review_source%3D24%26secure_username%3DV2998123a0eb2a87ee19f3581079ff66a5%26widget_env%3D100%26widget_id%3D2&widget_id=2"
  // useEffect(() => {
  //   const handleMessage = (event: MessageEvent) => {
  //     // 문자열로 들어오는 경우(크리마 위젯 등)
  //     if (typeof event.data === 'string') {
  //       if (event.data.includes('iframe_id=crema-product-reviews-3&act=open_new_review_popup')) {
  //         // 크리마에서 보내는 init_popup 메시지일 때만 처리
  //         console.log('Crema init_popup 메시지:', event.data)
  //         // 원하는 동작 추가
  //       }
  //     }
  //   }

  //   window.addEventListener('message', handleMessage)
  //   return () => window.removeEventListener('message', handleMessage)
  // }, [])

  return (
    <>
      {/* <div>
        {productItem && productItem.productNo && (
          <>
            <p>상품 리뷰 위젯</p>
            <div
              id='crema-product-reviews'
              className='crema-product-reviews'
              data-widget-id='2'
              data-widget-style='list'
              data-product-code={productItem.productNo}
            ></div>

            <p>전체 리뷰 위젯</p>
            <div className='crema-reviews' data-widget-id='2'></div>

            <p>내 리뷰 위젯</p>
            <div className='crema-reviews' data-type='managing-reviews'></div>

            <p>리뷰 작성하기 위젯</p>
            <button className='crema-new-review-link' data-product-code={productItem.productNo}>
              상품평 작성
            </button>
            <a
              className='crema-new-review-link'
              data-product-code={productItem.productNo}
              data-sub-order-validation='false'
              data-show-after-loading='true'
              style={{ display: 'none' }}
            >
              리뷰쓰기
            </a>

            <div className='crema-popup'></div>
          </>
        )}
      </div> */}
      <ProductDetailReviewWrapper ref={sectionRef} className='product_detail_review'>
        <p className='title'>상품 리뷰</p>
        <div className='contents'>
          <p className='title'>구매 만족도</p>
          <p className='star_rating'>
            {starCountArray.map((element: number, idx: number) => (
              <img src={`${imgURL}/${getStarType(product.reviewRate, idx)}.svg`} alt='별점' key={idx} />
            ))}
          </p>
          <p className='rating_num'>
            <span>
              <strong>{review?.rate}</strong>
            </span>
            <span>/</span>
            <span>5</span>
          </p>
          <BtnGrayRounded onClick={handleReviewWriteClick}>
            {isLoading ? (
              <SpinnerContainer>
                <LoadingSpinner />
              </SpinnerContainer>
            ) : (
              '리뷰 작성하기'
            )}
          </BtnGrayRounded>
        </div>
        {/* 리뷰 slect(mobile) */}
        <ProductDetailReviewList $ismore={isSort.toString()}>
          <div className='review_list_header'>
            <p className='title'>리뷰 {product.counter?.reviewCnt}건</p>
            <select
              name='review_select'
              id='review_select'
              onChange={(e) => {
                setReviewKeyword(e.target.value)
              }}
            >
              <option value='RECOMMEND'>추천순</option>
              <option value='REGISTER_YMDT'>등록순</option>
              <option value='RATING'>별점순</option>
              <option value='BEST_REVIEW'>좋아요순</option>
            </select>
          </div>

          {/* 리뷰 목록 */}
          <ul className='no_review_wrap'>
            {review?.items?.length === 0 ? (
              <NoReviewContainer>
                <NoReviewOuter>
                  <img src='/images/empty_logo.png' alt='' />
                  <NoReviewInner>상품 리뷰가 없습니다.</NoReviewInner>
                </NoReviewOuter>
              </NoReviewContainer>
            ) : (
              review?.items?.map((el: any, index: number) => {
                return <ProductReviewItem key={index} el={el} starCountArray={starCountArray} />
              })
            )}
          </ul>

          {/* 리뷰 페이지네이션 */}
          {pageCount.length === 0 || pageCount.length === 1 ? null : (
            <div className='pagination'>
              <button onClick={handlePrevClick} disabled={page === 1}>
                <img src={`${imgURL}/icon_arrow_prev.svg`} alt='Previous' />
              </button>

              {/* 앞쪽 생략 표시 */}
              {startPage > 1 && (
                <button onClick={() => setStartPage((prev) => Math.max(prev - maxPageButtons, 1))}>...</button>
              )}

              {/* 현재 페이지 그룹 표시 */}
              {renderPageGroupButtons()}

              {/* 뒤쪽 생략 표시 */}
              {startPage + maxPageButtons - 1 < totalPages && (
                <button onClick={() => setStartPage((prev) => prev + maxPageButtons)}>...</button>
              )}

              <button onClick={handleNextClick} disabled={page === totalPages}>
                <img src={`${imgURL}/icon_arrow_next.svg`} alt='Next' />
              </button>
            </div>
          )}
        </ProductDetailReviewList>
      </ProductDetailReviewWrapper>
      {isMobile && <Hr8></Hr8>}
      {/* 상품 Q&A 영역 */}
      <ProductDetailQna productItem={productItem} product={product} />
    </>
  )
}

export default memo(ProductDetailReviews)

// 상품 리뷰 영역
export const ProductDetailReviewWrapper = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  color: #898989;
  background: #fff;
  padding: 20px;

  /* 상품 리뷰 제목 */
  & > p.title {
    font-size: 15px;
    font-weight: 600;
    line-height: 16px;
    color: var(--colBlack);
    margin-bottom: 16px;
  }
  /* 구매 만족도 */
  & > div.contents {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    border-radius: 10px;
    background: var(--colGray1);
    padding: 16px 0;
    /* 구매 만족도 제목 */
    & > p.title {
      font-size: 16px;
      font-weight: 600;
      line-height: 20px;
      color: var(--colBlack);
      margin-bottom: 16px;
    }
    /* 구매만족도 별점 */
    & > .star_rating {
      display: flex;
      margin-bottom: 8px;
      & img {
        width: 18px;
        height: 18px;
      }
    }
    & > p.rating_num {
      display: flex;
      gap: 2px;
      font-size: 19px;
      font-weight: 400;
      line-height: 22px;
      color: #898989;
      margin-bottom: 20px;
      & strong {
        color: var(--colBlack);
        font-weight: 600;
      }
      & span {
        display: inline-block;
      }
    }
  }
  @media screen and (min-width: 768px) {
    font-size: 14px;
    font-weight: 400;
    line-height: 18px;
    color: #898989;
    background: #fff;
    padding: 32px 0 0 0;
    /* 상품 리뷰 제목 */
    & > p.title {
      font-size: 18px;
      font-weight: 600;
      line-height: 24px;
      color: var(--colBlack);
      margin-bottom: 16px;
    }
    /* 구매 만족도 */
    & > div.contents {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      border-radius: 10px;
      background: var(--colGray1);
      padding: 20px 0;
      /* 구매 만족도 제목 */
      & > p.title {
        font-size: 16px;
        font-weight: 600;
        line-height: 20px;
        color: var(--colBlack);
        margin-bottom: 16px;
      }
      /* 구매만족도 별점 */
      & > .star_rating {
        display: flex;
        margin-bottom: 8px;
        & img {
          width: 18px;
          height: 18px;
        }
      }
      & > p.rating_num {
        display: flex;
        gap: 2px;
        font-size: 19px;
        font-weight: 400;
        line-height: 22px;
        color: #898989;
        margin-bottom: 20px;
        & strong {
          color: var(--colBlack);
          font-weight: 600;
        }
        & span {
          display: inline-block;
        }
      }
    }
  }
`

// 리뷰 목록
export const ProductDetailReviewList = styled.div<{ $ismore: string }>`
  margin-top: 12px;
  /* 리뷰 목록 상단 */
  & > div.review_list_header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;

    /* 리뷰 목록 제목 */
    & > p.title {
      font-size: 15px;
      font-weight: 600;
      line-height: 16px;
      color: var(--colBlack);
    }
    /* 리뷰 정렬 셀렉트박스 */
    & > select {
      position: relative;
      width: auto;
      height: 32px;
      font-size: 13px;
      font-weight: 500;
      line-height: 20px;
      color: var(--colBlack);
      border: 1px #eaeaea solid;
      border-radius: 5px;
      padding: 0 34px 0 14px;
      -webkit-appearance: none;
      -moz-appearance: none;
      appearance: none;
      outline: none;
      z-index: 1;
    }
    & > select {
      background: url(${imgURL}/icon_arrow_down.png) no-repeat;
      background-size: 16px;
      background-position: calc(100% - 12px) center;
    }
  }
  /* 리뷰 목록 */
  & > ul {
    & li {
      padding: 16px 0;
      border-top: 1px #eaeaea solid;
      /* 리뷰 */
      & > .review {
        /* 리뷰 별점, 날짜 wrap */
        & > .review_txt_wrap {
          display: flex;
          justify-content: flex-start;
          align-items: center;
          margin-bottom: 16px;
          gap: 8px;
          /* 별점 */
          & > p.star_rating {
            display: flex;
            & img {
              width: 14px;
              height: 14px;
            }
            & > span.rating_num {
              font-size: 12px;
              font-weight: 600;
              line-height: 14px;
              color: var(--colBlack);
              margin-left: 6px;
            }
          }
          /* 날짜 */
          & > .date {
            position: relative;
            font-size: 12px;
            font-weight: 500;
            line-height: 14px;
            color: #898989;
            padding-left: 8px;
          }
          & > .date::before {
            content: '';
            display: block;
            position: absolute;
            top: 50%;
            left: 0;
            transform: translateY(-50%);
            width: 1px;
            height: 10px;
            background: var(--colGray3);
          }
        }
        /* 리뷰 내용 */
        & > .review_contents_wrap {
          & > .writer {
            font-size: 13px;
            font-weight: 600;
            line-height: 16px;
            color: var(--colBlack);
            margin-bottom: 10px;
          }
          & > p {
            font-size: 13px;
            font-weight: 400;
            line-height: 20px;
            color: var(--colBlack);
          }
          & > div.review_content {
            position: relative;
            white-space: normal;
            overflow: hidden;
            text-align: left;
            word-wrap: break-word;
            display: -webkit-box;
            -webkit-line-clamp: ${(props) => (props.$ismore ? '1000' : '2')};
            -webkit-box-orient: vertical;
            font-size: 13px;
            font-weight: 400;
            line-height: 20px;
            color: var(--colBlack);
            margin-bottom: 10px;
          }
          & > div.review_photo {
            display: flex;
            justify-content: flex-start;
            align-items: center;
            gap: 8px;
            margin-bottom: 16px;
            overflow-x: auto;
            & > p {
              & img {
                width: 80px;
                height: 80px;
                border-radius: 8px;
                border: 1px #eaeaea solid;
                object-fit: cover;
              }
            }
          }
          & > div.review_liked {
            display: flex;
            justify-content: flex-start;
            align-items: center;
            gap: 8px;
            & > p {
              font-size: 13px;
              font-weight: 400;
              line-height: 16px;
              color: #898989;
            }
          }
        }
      }
    }
    & li:last-of-type {
      border-bottom: 1px #eaeaea solid;
    }
  }
  /* 리뷰 페이지네이션 */
  & .pagination {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
    margin-top: 24px;

    /* 페이징 숫자 버튼 */
    & button {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 26px;
      height: 26px;
      font-size: 14px;
      font-weight: 500;
      line-height: 18px;
      color: #898989;
      background: none;
      border: none;
      cursor: pointer;
    }
    & button.on {
      color: var(--colBlack);
    }
    & button:first-of-type,
    & button:last-of-type {
      width: 30px;
      height: 30px;
      border-radius: 4px;
      border: 1px #eaeaea solid;
    }
  }
  @media screen and (min-width: 768px) {
    margin-top: 16px;
    /* 리뷰 목록 상단 */
    & > div.review_list_header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 12px;

      /* 리뷰 목록 제목 */
      & > p.title {
        font-size: 16px;
        font-weight: 600;
        line-height: 20px;
        color: var(--colBlack);
      }
      /* 리뷰 정렬 셀렉트박스 */
      & > select {
        position: relative;
        width: auto;
        font-size: 13px;
        font-weight: 500;
        line-height: 20px;
        color: var(--colBlack);
        background: #fff;
        border: 1px #eaeaea solid;
        border-radius: 4px;
        padding: 0 36px 0 14px;
        -webkit-appearance: none;
        -moz-appearance: none;
        appearance: none;
        outline: none;
      }
      & > select {
        background: url(${imgURL}/icon_arrow_down.png) no-repeat;
        background-size: 16px;
        background-position: calc(100% - 12px) center;
      }
    }
    /* 리뷰 목록 */
    & > ul {
      & li {
        padding: 20px 0;
        overflow: hidden;
        border-top: 1px var(--colGray3) solid;
        /* 리뷰 */
        & > .review {
          /* 리뷰 별점, 날짜 wrap */
          & > .review_txt_wrap {
            display: flex;
            justify-content: flex-start;
            align-items: center;
            margin-bottom: 16px;
            /* 별점 */
            & > p.star_rating {
              display: flex;
              justify-content: flex-start;
              align-items: center;
              & img {
                width: 16px;
                height: 16px;
              }
              & > span.rating_num {
                font-size: 14px;
                font-weight: 600;
                line-height: 20px;
                color: var(--colBlack);
                margin-left: 6px;
              }
            }
            /* 날짜 */
            & > .date {
              font-size: 13px;
              font-weight: 400;
              line-height: 20px;
              color: var(--colGray6);
            }
          }
          /* 리뷰 내용 */
          & > .review_contents_wrap {
            & > .writer {
              color: var(--colBlack);
              font-size: 13px;
              font-weight: 600;
              line-height: 16px;
              margin-bottom: 10px;
            }
            & > p {
              font-size: 13px;
              font-weight: 400;
              line-height: 20px;
              color: var(--colBlack);
            }
            & > div.review_content {
              position: relative;
              white-space: normal;
              overflow: hidden;
              text-align: left;
              word-wrap: break-word;
              display: -webkit-box;
              -webkit-line-clamp: ${(props) => (props.$ismore ? '1000' : '2')};
              -webkit-box-orient: vertical;
              font-size: 14px;
              font-weight: 400;
              line-height: 20px;
              color: var(--colBlack);
              margin-bottom: 10px;
            }
            & > div.review_photo {
              display: flex;
              justify-content: flex-start;
              align-items: center;
              gap: 8px;
              margin-bottom: 16px;
              overflow-x: auto;
              & > p {
                & img {
                  width: 80px;
                  height: 80px;
                  border-radius: 8px;
                  border: 1px #eaeaea solid;
                  object-fit: cover;
                }
              }
            }
            & > div.review_liked {
              display: flex;
              justify-content: flex-start;
              align-items: center;
              gap: 8px;
              & > p {
                font-size: 13px;
                font-weight: 400;
                line-height: 16px;
                color: var(--colGray6);
              }
            }
            /* & > p.review_content::after {
            content: '... 더보기';
            width: 50px;
            position: absolute;
            right: 0;
            bottom: 0;
            background-color: tomato;
            display: block;
          } */
          }
        }
      }
      & li:last-of-type {
        border-bottom: 1px #eaeaea solid;
      }
    }
    /* 리뷰 페이지네이션 */
    & .pagination {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 8px;
      margin-top: 24px;

      /* 페이징 숫자 버튼 */
      & button {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 26px;
        height: 26px;
        font-size: 14px;
        font-weight: 500;
        line-height: 18px;
        color: #898989;
        background: none;
        border: none;
        cursor: pointer;
      }
      & button.on {
        color: var(--colBlack);
      }
      & button:first-of-type,
      & button:last-of-type {
        width: 30px;
        height: 30px;
        border-radius: 4px;
        border: 1px #eaeaea solid;
      }
    }
  }
`

export const NoReviewContainer = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  width: 100%;
  height: 100%;

  @media screen and (min-width: 768px) {
    display: flex;
    padding: 0;
    height: 100%;
    justify-content: center;
    align-items: center;
  }
`

export const NoReviewOuter = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 80px 0 100px 0;
  & img {
    width: 85px;
    height: 85px;
  }
  @media screen and (min-width: 768px) {
    padding: 120px 0 160px 0;
    & img {
      width: 108px;
      height: 108px;
    }
  }
`
export const NoReviewInner = styled.p`
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  color: var(--colGray5);
  text-align: center;
  margin-bottom: 20px;
  @media screen and (min-width: 768px) {
    font-size: 20px;
    font-weight: 500;
    line-height: 32px;
  }
`

const SpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`

export const LoadingSpinner = styled.div`
  @keyframes spinner {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  display: flex;
  margin: auto;
  width: 20px;
  height: 20px;
  border: 3px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top-color: #898989;
  animation: spinner 1s ease infinite;

  @media screen and (min-width: 768px) {
    width: 20px;
    height: 20px;
  }
`
